import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function About() {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const aboutFaqs = [
    {
      q: '豆花视频网页版的优势是什么？',
      a: '豆花视频网页版最大的优势在于无需下载任何软件，直接在浏览器中就能观看海量高清视频。我们采用先进的视频传输技术，确保流畅的播放体验。同时支持多端访问，电脑、平板都能完美适配。',
    },
    {
      q: '视频内容更新频率如何？',
      a: '我们的内容团队每天都会更新视频资源，确保用户能够观看到最新的电视剧、电影、综艺等内容。热门节目通常在播出后24小时内同步上线。',
    },
    {
      q: '豆花视频网页版安全吗？',
      a: '豆花视频网页版高度重视用户的安全和隐私。我们不强制收集用户个人信息，不安装任何恶意软件，所有视频内容均经过安全审核，您可以放心使用。',
    },
    {
      q: '遇到播放问题怎么办？',
      a: '如果您在观看视频时遇到问题，可以尝试刷新页面、切换画质或更换浏览器。如果问题持续存在，请通过下方联系方式与我们取得联系，我们将在24小时内为您解决。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      {/* AboutPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于豆花视频网页版',
            description: '豆花视频网页版品牌介绍、发展历程与联系方式，了解我们的使命与价值观。',
            url: 'https://www.douhuavideo.com/about',
            inLanguage: 'zh-CN',
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '豆花视频首页',
                item: 'https://www.douhuavideo.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-16 md:py-20 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500 mx-auto mb-6">
                <i className="ri-play-fill text-3xl text-background-50"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于豆花视频
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                致力于成为最受欢迎的免费在线视频平台，让每个人都能轻松享受高清观影体验
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
                <div>
                  <span className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-3 block">品牌故事</span>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-6 leading-tight">
                    从零开始，<br />只为更好的观影体验
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      豆花视频诞生于2020年，创始团队由一群热爱影视的互联网人组成。我们发现很多用户受限于设备性能和存储空间，无法流畅地观看高清视频，于是萌生了打造一个纯网页端视频平台的想法。
                    </p>
                    <p>
                      经过多年的技术积累和产品打磨，豆花视频网页版已经成长为一个拥有海量视频资源、日均服务千万用户的在线视频平台。我们始终坚持"无需下载、即点即看"的产品理念，让每个人都能轻松享受在线观影的乐趣。
                    </p>
                    <p>
                      未来，豆花视频将继续深耕视频技术领域，优化播放体验，丰富内容资源，为用户提供更加优质的在线观影服务。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20working%20in%20bright%20open%20office%20with%20large%20screens%20displaying%20video%20content%2C%20collaborative%20workspace%2C%20warm%20natural%20light%2C%20plants%2C%20creative%20atmosphere%2C%20tech%20company%20culture%2C%20friendly%20team%20environment&width=800&height=600&seq=douhua-about-brand&orientation=landscape"
                    alt="豆花视频团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  我们的核心优势
                </h2>
                <p className="text-sm text-foreground-600">为什么选择豆花视频网页版</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  {
                    icon: 'ri-flashlight-line',
                    title: '无需下载',
                    desc: '纯网页端体验，无需安装任何软件或插件，打开浏览器即点即看。',
                  },
                  {
                    icon: 'ri-4k-line',
                    title: '高清画质',
                    desc: '支持1080P超清画质，先进视频编码技术确保流畅播放体验。',
                  },
                  {
                    icon: 'ri-device-line',
                    title: '多端适配',
                    desc: '完美适配电脑、平板等多种设备，随时随地畅享视频内容。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '安全可靠',
                    desc: '严格的内容审核机制，保护用户隐私，打造安全健康的观影环境。',
                  },
                ].map((value, i) => (
                  <div
                    key={i}
                    className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
                      <i className={`${value.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">
                      {value.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {value.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* User Reviews */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-600">来自真实用户的评价</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  {
                    name: '影视爱好者小陈',
                    role: '忠实用户 · 使用2年',
                    content: '豆花视频真的是我用过最方便的视频平台，不用下载APP，电脑上直接就能看，画质也很清晰。每天都会打开看看有什么新内容，已经成为生活的一部分了。',
                    rating: 5,
                  },
                  {
                    name: '追剧达人阿琳',
                    role: '忠实用户 · 使用1年',
                    content: '作为一个重度追剧用户，豆花视频的分类导航特别好用，想看什么类型一键就能找到。更新速度也很快，热播剧基本都能及时看到，强烈推荐！',
                    rating: 5,
                  },
                  {
                    name: '学生党小明',
                    role: '忠实用户 · 使用6个月',
                    content: '宿舍网速一般，但豆花视频播放特别流畅，几乎不卡顿。而且完全免费，对于我们学生来说太友好了。动漫资源也很多，每周追番必备。',
                    rating: 5,
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="p-6 rounded-xl bg-background-100 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: review.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{review.content}"
                    </p>
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-500">{review.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  发展历程
                </h2>
                <p className="text-sm text-foreground-600">豆花视频的成长之路</p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2"></div>
                <div className="space-y-8">
                  {[
                    { year: '2020', title: '项目启动', desc: '创始团队成立，开始研发豆花视频网页版的核心技术。' },
                    { year: '2021', title: '正式上线', desc: '豆花视频网页版1.0正式发布，支持基本的视频播放和分类浏览功能。' },
                    { year: '2022', title: '用户突破', desc: '月活跃用户突破100万，平台内容资源扩展到5000+部。' },
                    { year: '2023', title: '技术升级', desc: '全面升级视频编码技术，支持1080P高清播放，优化多端适配体验。' },
                    { year: '2024', title: '快速发展', desc: '日均观看量突破1000万次，内容覆盖八大分类，成为行业领先平台。' },
                    { year: '2025', title: '持续创新', desc: '引入智能推荐算法，个性化内容推荐更精准，用户体验全面升级。' },
                  ].map((item, i) => (
                    <div key={i} className="relative flex items-start gap-6 md:gap-0">
                      <div className={`md:w-1/2 ${i % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:ml-auto'}`}>
                        <div className={`p-5 rounded-xl bg-background-50 border border-background-200/70 ${
                          i % 2 !== 0 ? '' : ''
                        }`}>
                          <div className="text-xs font-semibold text-accent-600 mb-1">{item.year}</div>
                          <h3 className="text-base font-semibold text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-xs text-foreground-600">{item.desc}</p>
                        </div>
                      </div>
                      <div className="absolute left-4 md:left-1/2 top-0 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 mt-6"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-600">有任何问题或建议？欢迎随时与我们联系</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">support@douhuavideo.com</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mx-auto mb-4">
                    <i className="ri-wechat-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">微信公众号</h3>
                  <p className="text-sm text-foreground-600">搜索"豆花视频"关注我们</p>
                </div>
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mx-auto mb-4">
                    <i className="ri-customer-service-2-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">在线客服</h3>
                  <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600">了解更多关于豆花视频的信息</p>
              </div>
              <div className="space-y-3">
                {aboutFaqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all ${
                        activeFaq === index
                          ? 'bg-primary-500 text-background-50 rotate-180'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </div>
                    </button>
                    {activeFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQPage Schema */}
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: aboutFaqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.q,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.a,
                  },
                })),
              }),
            }}
          />
        </div>
      </main>

      <Footer />
    </>
  );
}